<template>
  <div
    v-if="showProperty"
    :class="outerClass"
  >
    <p v-if="propertyName === 'customerStreet'"
      :class="['p-0 text-blue text-sm', visible, legendClass, labelClass]"
    >
      {{ label }}
    </p>
    <label v-else
      :id="propertyName + '-label'"
      :for="propertyName"
      :class="['p-0 text-blue text-sm', visible, legendClass, labelClass]"
    >
      {{ label }}
    </label>
    <div v-if="formType === 'email'" data-suggestion-block>
      <input
        :class="[invalidClass, inputClass]"
        :type="formType"
        :id="propertyName"
        v-model.trim="fieldValue"
        :name="propertyName"
        :placeholder="placeholder"
        :autocomplete="autocomplete"
        :ref="propertyName"
        @focus="$emit('focus')"
        @blur="$emit('blur')"
        data-check-domain
        :data-suggestion-message="$t('messages_nl.label.cancellation.userLetter.typo-suggestion-message')"
        @change="handleUpdate()"
        :aria-labelledby="propertyName + '-label'"
        :[ariaError]="propertyName + '-error'"
        :aria-invalid="showError ? 'true' : 'false'"
      />
    </div>
    <div v-else-if="'customerStreet' === propertyName && !isBelgian || 'customerCity' === propertyName"
      :class="showError && specialError && 'customerStreet' === propertyName ? 'h-[3.125rem]' : [invalidClass, inputClass]"
    >
      <adivare-alert v-if="showError && specialError && 'customerStreet' === propertyName"
        :messages="errorMessage"
        size="xs"
        show-icon
      />
      <span v-else
        :class="addressPreviewClass"
        v-html="addressPreview"
      />
    </div>
    <template v-else-if="propertyName === 'customerHouseNumber'">
      <input
        :class="[invalidClass, inputClass]"
        :type="formType"
        :id="propertyName"
        :name="propertyName"
        :placeholder="placeholder"
        :autocomplete="autocomplete"
        :ref="propertyName"
        v-model.trim="houseNumber"
        @focus="$emit('focus')"
        @blur="$emit('blur')"
        @change="splitHouseNumber($event.target.value)"
        :aria-labelledby="propertyName + '-label'"
        :[ariaError]="propertyName + '-error'"
        :aria-invalid="showError ? 'true' : 'false'"
      />

      <!-- hidden street field to have browsers autofill house number -->
      <input
        class="hidden"
        type="text"
        name="street"
        id="street"
      />
    </template>
    <input v-else-if="formType !== 'textarea'"
      :class="[invalidClass, inputClass]"
      :type="formType"
      :id="propertyName"
      v-model.trim="fieldValue"
      :name="propertyName"
      :placeholder="placeholder"
      :autocomplete="autocomplete"
      :ref="propertyName"
      @focus="$emit('focus')"
      @blur="$emit('blur')"
      :aria-labelledby="propertyName + '-label'"
      :[ariaError]="propertyName + '-error'"
      :aria-invalid="showError ? 'true' : 'false'"
    />
    <textarea v-else
      :class="[inputClass, invalidClass]"
      :id="propertyName"
      v-model.trim="fieldValue"
      :name="propertyName"
      :placeholder="placeholder"
      :autocomplete="autocomplete"
      :ref="propertyName"
      @focus="$emit('focus')"
      @blur="$emit('blur')"
      :rows="rows"
      :required="!optional"
      :aria-labelledby="propertyName + '-label'"
      :[ariaError]="propertyName + '-error'"
      :aria-invalid="showError ? 'true' : 'false'"
    />
    <slot />
    <div
      :id="propertyName + '-error'"
    >
      <adivare-alert
        v-if="showError && !specialError"
        class="mt-1"
        :messages="errorMessage"
        size="xs"
        show-icon
      />
    </div>
  </div>
</template>

<script>
import { empty } from '../../../../js/util/util-functions';
import AdivareAlert from '../../../components/alert';
import AdivarePreviewElement from '../letter/partials/preview-element';

const inputTypes = [
  'button',
  'checkbox',
  'color',
  'date',
  'datetime-local',
  'email',
  'file',
  'hidden',
  'image',
  'month',
  'number',
  'password',
  'radio',
  'range',
  'reset',
  'search',
  'submit',
  'tel',
  'text',
  'time',
  'url',
  'week',
  'textarea',
];
//TODO make this generic, its not re-usable now. See if it can integrate with symfony's form object.
export default {
  name: "adivare-input",
  components: {
    AdivareAlert,
    AdivarePreviewElement,
  },
  emits: [
    'validationRequest',
    'update:modelValue',
    'focus',
    'blur',
  ],
  data() {
    return {
      houseNumber: '',
    };
  },
  props: {
    outerClass: {
      required: false,
      default: null,
      type: String,
    },
    legendClass: {
      required: false,
      default: null,
      type: String,
    },
    showProperty: {
      required: false,
      default: true,
      type: Boolean,
    },
    propertyName: {
      required: true,
      type: String
    },
    labelClass: {
      required: true,
      type: String,
    },
    formType: {
      required: true,
      validator(value) {
        return undefined === value || inputTypes.includes(value);
      }
    },
    inputClass: {
      required: false,
      default: null,
      type: String,
    },
    fieldLabel: {
      required: true,
      type: String,
    },
    legendTransKey: {
      required: false,
      type: String,
    },
    autocomplete: {
      required: false,
      type: String,
    },
    minlength: {
      required: false,
      type: Number,
      default: null,
    },
    maxlength: {
      required: false,
      type: Number,
      default: null,
    },
    optional: {
      required: false,
      type: Boolean,
      default: false,
    },
    rows: {
      required: false,
      type: Number,
    },
    customErrorMessage: {
      required: false,
      type: String,
      default: null,
    },
    modelValue: {
      required: false,
    },
    placeholder: {
      required: false,
      type: String,
    },
    specialError: {
      required: false,
      type: Boolean,
    },
    inputMode: {
      required: false,
      type: String,
    },
    pattern: {
      required: false,
      type: String,
    },
  },
  computed: {
    isBelgian() {
      return true === this.$store.getters.getCountry.belgium;
    },
    required() {
      return this.optional ? null : true;
    },
    ariaError() {
      if ('string' === typeof this.errorMessage || 1 === this.errorMessage.length) return 'aria-describedby';
      if (1 < errorMessage.length) return 'aria-details';
      return null;
    },
    fieldValue: {
      get() {
        return this.$store.getters.getFormField(this.propertyName);
      },
      set(value) {
        if (!value) value = null;
        this.$store.dispatch('updateFormField', {
          name: this.propertyName,
          value,
        })
        this.$emit('validationRequest', value);
        this.$emit('update:modelValue', value);
      },
    },
    addressPreview() {
      if (!this.fieldValue) return this.label;
      return this.fieldValue;
    },
    addressPreviewClass() {
      if (this.fieldValue) return;
      return 'text-fontcolor-light-grey';
    },
    label: {
      get() {
        if (this.legendTransKey === undefined) {
          return this.fieldLabel;
        }

        return this.$t(this.legendTransKey);
      },
    },
    visible() {
      if (!this.fieldValue) return 'invisible';
      return 'visible';
    },
    fieldIsValid() {
      // Either the field is validated, or it is optional and empty.
      return this.$store.getters.getFormFieldValidityState(this.propertyName) ||
        (true === this.optional && empty(this.fieldValue));
    },
    /**
     * Determines whether this field should show an error.
     * This depends on the type of submit that has ben done (either, the cancellation, or the pdf download).
     * @returns {boolean}
     */
    showError() {
      if (Object.keys(this.$store.getters.getPaymentType).includes(this.propertyName)) {
        // This is a payment type only field, it does not apply to pdf download.
        return true === this.$store.getters.showPaymentTypeErrors && false === this.fieldIsValid;
      }

      return true === this.$store.getters.showErrors &&
        false === this.fieldIsValid;
    },
    invalidClass() {
      return true === this.showError ? 'is-invalid' : '';
    },
    errorMessage() {
      if (this.specialError) return this.$t('messages_nl.global.pro6ppError');
      if ('customerHouseNumber' === this.propertyName && this.houseNumber)
        return this.$t('messages_nl.global._incorrect', { 'field': this.fieldLabel });
      if (!this.fieldValue) return this.$t('messages_nl.global.required');
      if (this.customErrorMessage) return this.$t(this.customErrorMessage);
      return this.$t('messages_nl.global._incorrect', { 'field': this.fieldLabel });
    },
  },
  created() {
    this.houseNumber = this.getHouseNumber();
  },
  mounted() {
    this.$store.commit('setInputRef', {
      name: this.propertyName,
      ref: this.$refs[this.propertyName],
    });
  },
  unmounted() {
    this.$store.commit('freeInputRef', this.propertyName);
  },
  methods: {
    handleUpdate() {
      if (this.fieldValue === this.$refs[this.propertyName].value) return;
      this.fieldValue = this.$refs[this.propertyName].value;
    },
    getHouseNumber() {
      const number = this.$store.getters.getFormField('customerHouseNumber');
      const extension = this.$store.getters.getFormField('customerHouseNumberExtension');
      // add dash if extension contains numbers
      if (extension) return /\d/.test(extension) ? `${number}-${extension}` : `${number}${extension}`;
      return number;
    },
    splitHouseNumber(input) {
      const regExp = /^(?<number>[1-9][0-9]*)-?\/?\s?(?<extension>[a-zA-Z0-9-\/\s]+)*$/;
      const match = input.toString().match(regExp);
      const fields = {
        customerHouseNumber: match ? match.groups.number : null,
        customerHouseNumberExtension: match ? match.groups.extension : null,
      };
      for (const [key, value] of Object.entries(fields)) {
        this.$store.dispatch('updateFormField', {
          name: key,
          value,
        })
      }
      this.$emit('validationRequest', !!match);
      this.$emit('update:modelValue', fields.customerHouseNumber);
    },
  },
}
</script>
